import React, { useState, useEffect, useMemo } from 'react';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import api from '../utils/api';
import AppLoadingState from './common/AppLoadingState';
import { AppPageShell, PageHeader } from './common/ui';

function formatMinutesLabel(credits) {
  const n = Number(credits);
  if (!Number.isFinite(n)) return '—';
  if (Number.isInteger(n)) return `${n}`;
  return n.toFixed(2);
}

function isFeaturedTier(name) {
  return /^growth$/i.test((name || '').trim());
}

const Credits = () => {
  const [loading, setLoading] = useState(true);
  const [creditData, setCreditData] = useState({
    balance: 0,
    packages: [],
    transactions: []
  });

  useEffect(() => {
    const fetchCreditData = async () => {
      try {
        setLoading(true);
        const [balance, packages, transactions] = await Promise.all([
          api.getCreditBalance(),
          api.getCreditPackages(),
          api.getCreditTransactions()
        ]);

        setCreditData({
          balance: balance.credits,
          packages: packages || [],
          transactions: transactions || []
        });
      } catch (error) {
        console.error('Error fetching credit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreditData();
  }, []);

  const orderedPackages = useMemo(() => {
    return [...creditData.packages].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  }, [creditData.packages]);

  const featuredId = useMemo(() => {
    const featured = orderedPackages.find((p) => isFeaturedTier(p.name));
    return featured ? featured.id : null;
  }, [orderedPackages]);

  if (loading) {
    return <AppLoadingState layout="section" showLabel={false} />;
  }

  return (
    <AppPageShell maxWidthClass="max-w-6xl">
      <PageHeader
        eyebrow="Billing"
        title="Credits"
        description="Check your remaining call time, compare packages, and review credit movements."
      />
      <div className="space-y-8">
      {/* Credit Balance */}
      <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-6 shadow-[var(--app-panel-shadow)]">
        <h2 className="text-lg font-semibold text-[var(--app-ink)]">Credit balance</h2>
        <div className="mt-4">
          <p className="text-4xl font-bold text-[var(--app-accent)]">
            {(() => {
              const totalMinutes = creditData.balance;
              const hours = Math.floor(totalMinutes / 60);
              const minutes = Math.floor(totalMinutes % 60);
              const seconds = Math.round((totalMinutes % 1) * 60);
              
              if (hours > 0) {
                return `${hours}h ${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
              }
              if (minutes > 0) {
                return `${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}`;
              }
              return seconds > 0 ? `${seconds}s` : '0m';
            })()}
          </p>
          <p className="text-[var(--app-muted)]">available time</p>
        </div>
      </div>

      {/* Credit Packages */}
      <div className="rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] p-6 shadow-[var(--app-panel-shadow)] sm:p-8">
        <div className="mb-8 max-w-2xl">
          <h2 className="text-lg font-semibold tracking-tight text-[var(--app-ink)]">Choose your AI caller plan</h2>
          <p className="mt-2 text-sm leading-relaxed text-[var(--app-muted)]">
            Monthly plans include bundled minutes for AI calls. Higher tiers improve your effective per-minute rate for
            qualification, booking, reminders, and follow-up workflows.
          </p>
        </div>

        {orderedPackages.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-[var(--app-border)] bg-[var(--app-surface-strong)] px-4 py-8 text-center text-sm text-[var(--app-muted)]">
            No packages are available right now. Check back soon or contact support.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 xl:grid-cols-4 xl:gap-5 items-stretch">
            {orderedPackages.map((pkg) => {
              const price = parseFloat(pkg.price_usd);
              const credits = parseFloat(pkg.credits);
              const safePrice = Number.isFinite(price) ? price : 0;
              const safeCredits = Number.isFinite(credits) && credits > 0 ? credits : 0;
              const perMinute = safeCredits > 0 ? safePrice / safeCredits : null;
              const featured = featuredId != null && pkg.id === featuredId;
              const minutesPrimaryLabel = `${formatMinutesLabel(pkg.credits)} minutes included`;
              const cardDescription = pkg.description || '';

              return (
                <div
                  key={pkg.id}
                  className={[
                    'group relative flex h-full flex-col overflow-hidden rounded-2xl border p-6 transition duration-200',
                    'bg-[var(--app-surface-strong)] shadow-[var(--app-panel-shadow)]',
                    featured
                      ? 'border-[var(--app-accent)] ring-2 ring-[var(--app-accent-ring)] z-[1]'
                      : 'border-[var(--app-border)] hover:border-[var(--app-accent)]/35',
                    'hover:-translate-y-0.5 hover:shadow-[0_28px_80px_rgba(15,23,42,0.12)]',
                    'dark:hover:shadow-[0_28px_80px_rgba(0,0,0,0.45)]'
                  ].join(' ')}
                >
                  {featured ? (
                    <div className="absolute right-0 top-0 rounded-bl-xl bg-[var(--app-accent-soft)] px-3 py-1 text-[0.65rem] font-semibold tracking-wide text-[var(--app-accent)]">
                      Best value
                    </div>
                  ) : null}

                  <div className="pr-12">
                    <h3 className="text-base font-semibold text-[var(--app-ink)]">{pkg.name}</h3>
                    <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[var(--app-muted)]">
                      Monthly plan
                    </p>
                  </div>

                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="text-3xl font-bold tabular-nums tracking-tight text-[var(--app-accent)]">
                      ${safePrice.toFixed(2)}
                    </span>
                    <span className="text-sm text-[var(--app-muted)]">/ mo</span>
                  </div>

                  <div className="mt-5 space-y-3 rounded-xl border border-[var(--app-border)] bg-[var(--app-accent-soft)]/40 px-3 py-3 dark:bg-[var(--app-surface)]/80">
                    <div className="flex items-start gap-2.5">
                      <AccessTimeRoundedIcon className="mt-0.5 shrink-0 text-[var(--app-accent)]" sx={{ fontSize: 20 }} />
                      <div>
                        <p className="text-sm font-medium text-[var(--app-ink)]">{minutesPrimaryLabel}</p>
                        {perMinute != null ? (
                          <p className="mt-0.5 text-xs text-[var(--app-muted)]">
                            ≈ ${perMinute.toFixed(2)} per minute
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {cardDescription ? (
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-[var(--app-muted)]">{cardDescription}</p>
                  ) : (
                    <div className="flex-1" />
                  )}

                  <div className="mt-auto w-full shrink-0 pt-5">
                    <button
                      type="button"
                      onClick={() => {/* TODO: Implement purchase */}}
                      className={[
                        'w-full rounded-xl py-2.5 text-sm font-semibold transition-colors',
                        'bg-[var(--app-accent)] text-[var(--app-accent-strong)]',
                        'hover:bg-[var(--app-accent-hover)]',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-accent)]'
                      ].join(' ')}
                    >
                      Subscribe
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-[var(--app-muted)]">
          Monthly included minutes reset every billing cycle and do not roll over. Extra prepaid credits stay on your
          balance until used.
        </p>
      </div>

      {/* Transaction History */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--app-ink)] mb-4">Transaction history</h2>
        <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] overflow-hidden shadow-[var(--app-panel-shadow)]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {creditData.transactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(transaction.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      transaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {transaction.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {parseFloat(transaction.amount).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {transaction.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </AppPageShell>
  );
};

export default Credits;
